import React from "react";
import List from "@mui/material/List";
import Box from "@mui/material/Box";
import ListItemIcon from "@mui/material/ListItemIcon";
import InfoIcon from "@mui/icons-material/Info";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import ListItemButton from "@mui/material/ListItemButton";
import ListSubheader from "@mui/material/ListSubheader";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import SpaIcon from "@mui/icons-material/Spa";
import {useLocation, useNavigate} from "react-router";
import {MENU_CLOSED, MENU_OPENED, useHeaderDispatch, useHeaderState} from "../header/headerContext";
import {useCheckIfAdmin} from "../firebase/useCheckIfAdmin";
import {useFirebaseState} from "../firebase/firebaseContext";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import ExploreIcon from "@mui/icons-material/Explore";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import {AccountCircle, ContactPage, Email, Folder, Newspaper as NewspaperIcon, People} from "@mui/icons-material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SchoolIcon from "@mui/icons-material/School";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import {useIsAuthenticated} from "../firebase/useIsAuthenticated";
import {useIsInstructorOrAdmin} from "../firebase/useIsInstructorOrAdmin";

const drawerWidth = 280;

export const Menu = () => {
    const {menuOpen} = useHeaderState();
    const dispatch = useHeaderDispatch();
    const navigate = useNavigate();
    let auth = null;
    try {
        const fb = useFirebaseState();
        auth = fb?.auth;
    } catch {
        auth = null;
    }
    const isAdmin = useCheckIfAdmin();
    const location = useLocation();
    const authenticated = useIsAuthenticated();
    const instructorOrAdmin = useIsInstructorOrAdmin();

    const handleClick = (url) => {
        dispatch({type: MENU_CLOSED});
        navigate(url);
    };

    const handleClose = () => {
        dispatch({type: MENU_CLOSED});
    };

    const userStatusCard = () => {
        if (!authenticated || !auth?.currentUser) return null;
        const currentUser = auth.currentUser;
        const name = currentUser.displayName || "Innlogget bruker";
        const email = currentUser.email || "";

        return (
            <Box
                onClick={() => handleClick("/me")}
                sx={{
                    mx: 2,
                    mb: 2,
                    p: 1.75,
                    borderRadius: 3,
                    bgcolor: "rgba(40, 82, 56, 0.06)",
                    border: "1px solid rgba(40, 82, 56, 0.12)",
                    cursor: "pointer",
                    transition: "all 0.15s ease-in-out",
                    "&:hover": {
                        bgcolor: "rgba(40, 82, 56, 0.1)",
                        borderColor: "primary.main",
                    },
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: 1.5, mb: 0.5}}>
                    <Avatar
                        src={currentUser.photoURL}
                        alt={name}
                        sx={{
                            width: 38,
                            height: 38,
                            border: "2px solid",
                            borderColor: "primary.main",
                        }}
                    />
                    <Box sx={{minWidth: 0, flex: 1}}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 700,
                                color: "text.primary",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {name}
                        </Typography>
                        <Chip
                            label={isAdmin ? "Administrator" : instructorOrAdmin ? "Kursholder" : "Bruker"}
                            size="small"
                            sx={{
                                height: 18,
                                fontSize: "0.68rem",
                                fontWeight: 700,
                                bgcolor: "primary.main",
                                color: "common.white",
                            }}
                        />
                    </Box>
                </Box>
                {email && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pl: 0.25}}
                    >
                        {email}
                    </Typography>
                )}
            </Box>
        );
    };

    const getItemStyles = (isSelected) => ({
        mx: 1.5,
        my: 0.35,
        px: 1.75,
        py: 1,
        borderRadius: 2.5,
        minHeight: 44,
        color: isSelected ? "primary.main" : "text.primary",
        bgcolor: isSelected ? "rgba(40, 82, 56, 0.1)" : "transparent",
        "& .MuiListItemIcon-root": {
            color: isSelected ? "primary.main" : "text.secondary",
            minWidth: 38,
        },
        "& .MuiListItemText-primary": {
            fontWeight: isSelected ? 700 : 500,
            fontSize: "0.925rem",
        },
        "&:hover": {
            bgcolor: isSelected ? "rgba(40, 82, 56, 0.14)" : "rgba(40, 82, 56, 0.05)",
            "& .MuiListItemIcon-root": {
                color: "primary.main",
            },
        },
        "&:focus-visible": {
            outline: "2px solid #285238",
            outlineOffset: "2px",
        },
    });

    const getSubheaderStyles = () => ({
        bgcolor: "transparent",
        px: 2.5,
        pt: 1.5,
        pb: 0.5,
        fontWeight: 700,
        fontSize: "0.72rem",
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "primary.main",
        lineHeight: "normal",
    });

    const adminMenu = () => {
        if (!isAdmin) return null;
        return (
            <>
                <Divider sx={{my: 1, borderColor: "rgba(40, 82, 56, 0.08)"}} />
                <List subheader={<ListSubheader sx={getSubheaderStyles()}>Admin</ListSubheader>}>
                    <ListItemButton
                        onClick={() => handleClick("/admin/users")}
                        selected={"/admin/users" === location.pathname}
                        sx={getItemStyles("/admin/users" === location.pathname)}
                    >
                        <ListItemIcon>
                            <People />
                        </ListItemIcon>
                        <ListItemText primary="Brukere" />
                    </ListItemButton>
                    <ListItemButton
                        onClick={() => handleClick("/administrate-instructors")}
                        selected={"/administrate-instructors" === location.pathname}
                        sx={getItemStyles("/administrate-instructors" === location.pathname)}
                    >
                        <ListItemIcon>
                            <SupervisorAccountIcon />
                        </ListItemIcon>
                        <ListItemText primary="Kursholdere" />
                    </ListItemButton>
                    <ListItemButton
                        onClick={() => handleClick("/admin/training")}
                        selected={"/admin/training" === location.pathname}
                        sx={getItemStyles("/admin/training" === location.pathname)}
                    >
                        <ListItemIcon>
                            <SchoolIcon />
                        </ListItemIcon>
                        <ListItemText primary="Kurslederopplæring" />
                    </ListItemButton>
                    <ListItemButton
                        onClick={() => handleClick("/admin/news")}
                        selected={"/admin/news" === location.pathname}
                        sx={getItemStyles("/admin/news" === location.pathname)}
                    >
                        <ListItemIcon>
                            <NewspaperIcon />
                        </ListItemIcon>
                        <ListItemText primary="Nyheter" />
                    </ListItemButton>
                    <ListItemButton
                        onClick={() => handleClick("/send-email-to-instructors")}
                        selected={"/send-email-to-instructors" === location.pathname}
                        sx={getItemStyles("/send-email-to-instructors" === location.pathname)}
                    >
                        <ListItemIcon>
                            <Email />
                        </ListItemIcon>
                        <ListItemText primary="Epost" />
                    </ListItemButton>
                </List>
            </>
        );
    };

    const profileMenuItem = () => {
        if (!authenticated) return null;
        return (
            <ListItemButton
                onClick={() => handleClick("/me")}
                selected={"/me" === location.pathname}
                sx={getItemStyles("/me" === location.pathname)}
            >
                <ListItemIcon>
                    <AccountCircle />
                </ListItemIcon>
                <ListItemText primary="Profil" />
            </ListItemButton>
        );
    };

    const filesMenu = () => {
        if (!instructorOrAdmin) return null;
        return (
            <>
                <Divider sx={{my: 1, borderColor: "rgba(40, 82, 56, 0.08)"}} />
                <List subheader={<ListSubheader sx={getSubheaderStyles()}>Instruktørverktøy</ListSubheader>}>
                    <ListItemButton
                        onClick={() => handleClick("/me")}
                        selected={"/me" === location.pathname}
                        sx={getItemStyles("/me" === location.pathname)}
                    >
                        <ListItemIcon>
                            <EventAvailableIcon />
                        </ListItemIcon>
                        <ListItemText primary="Mine kurs & henvendelser" />
                    </ListItemButton>
                    <ListItemButton
                        onClick={() => handleClick("/instructor-profile")}
                        selected={"/instructor-profile" === location.pathname}
                        sx={getItemStyles("/instructor-profile" === location.pathname)}
                    >
                        <ListItemIcon>
                            <ContactPage />
                        </ListItemIcon>
                        <ListItemText primary="Instruktørprofil" />
                    </ListItemButton>
                    <ListItemButton
                        onClick={() => handleClick("/files")}
                        selected={"/files" === location.pathname}
                        sx={getItemStyles("/files" === location.pathname)}
                    >
                        <ListItemIcon>
                            <Folder />
                        </ListItemIcon>
                        <ListItemText primary="Filer" />
                    </ListItemButton>
                </List>
            </>
        );
    };

    const menuList = (
        <>
            <List>
                <ListItemButton
                    onClick={() => handleClick("/about")}
                    selected={"/about" === location.pathname}
                    sx={getItemStyles("/about" === location.pathname)}
                >
                    <ListItemIcon>
                        <InfoIcon />
                    </ListItemIcon>
                    <ListItemText primary="Info om kurset" />
                </ListItemButton>
                <ListItemButton
                    selected={"/meditation" === location.pathname}
                    onClick={() => handleClick("/meditation")}
                    sx={getItemStyles("/meditation" === location.pathname)}
                >
                    <ListItemIcon>
                        <SelfImprovementIcon />
                    </ListItemIcon>
                    <ListItemText primary="Meditasjon" />
                </ListItemButton>
                <ListItemButton
                    onClick={() => handleClick("/#erfaringer")}
                    selected={location.pathname === "/" && location.hash === "#erfaringer"}
                    sx={getItemStyles(location.pathname === "/" && location.hash === "#erfaringer")}
                >
                    <ListItemIcon>
                        <FormatQuoteIcon />
                    </ListItemIcon>
                    <ListItemText primary="Erfaringer fra fagfolk" />
                </ListItemButton>
                <ListItemButton
                    selected={"/find-instructor" === location.pathname || "/regular" === location.pathname}
                    onClick={() => handleClick("/find-instructor")}
                    sx={getItemStyles("/find-instructor" === location.pathname || "/regular" === location.pathname)}
                >
                    <ListItemIcon>
                        <ExploreIcon />
                    </ListItemIcon>
                    <ListItemText primary="Finn kursholder" />
                </ListItemButton>
                <ListItemButton
                    selected={"/calendar" === location.pathname || "/kurskalender" === location.pathname}
                    onClick={() => handleClick("/calendar")}
                    sx={getItemStyles("/calendar" === location.pathname || "/kurskalender" === location.pathname)}
                >
                    <ListItemIcon>
                        <CalendarMonthIcon />
                    </ListItemIcon>
                    <ListItemText primary="Kurskalender" />
                </ListItemButton>
                <ListItemButton
                    onClick={() => handleClick("/news")}
                    selected={"/news" === location.pathname}
                    sx={getItemStyles("/news" === location.pathname)}
                >
                    <ListItemIcon>
                        <NewspaperIcon />
                    </ListItemIcon>
                    <ListItemText primary="Aktuelt" />
                </ListItemButton>
                <ListItemButton
                    onClick={() => handleClick("/get-educated")}
                    selected={"/get-educated" === location.pathname}
                    sx={getItemStyles("/get-educated" === location.pathname)}
                >
                    <ListItemIcon>
                        <MedicalServicesIcon />
                    </ListItemIcon>
                    <ListItemText primary="Bli kursholder" />
                </ListItemButton>
                <ListItemButton
                    onClick={() => handleClick("/order-books")}
                    selected={"/order-books" === location.pathname}
                    sx={getItemStyles("/order-books" === location.pathname)}
                >
                    <ListItemIcon>
                        <LocalLibraryIcon />
                    </ListItemIcon>
                    <ListItemText primary="Bestill bøker" />
                </ListItemButton>
                {profileMenuItem()}
            </List>
            {filesMenu()}
        </>
    );

    const renderHeader = () => (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 2,
                borderBottom: "1px solid rgba(40, 82, 56, 0.08)",
                mb: 1.5,
            }}
        >
            <Box
                role="button"
                aria-label="Kurs i bekymringsmestring logo"
                tabIndex={0}
                onClick={() => handleClick("/")}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleClick("/");
                    }
                }}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    outline: "none",
                    borderRadius: 2,
                    "&:focus-visible": {
                        outline: "2px solid #285238",
                        outlineOffset: "2px",
                    },
                }}
            >
                <SpaIcon sx={{fontSize: 26, color: "primary.main"}} />
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 800,
                        fontSize: "1.05rem",
                        color: "primary.main",
                        letterSpacing: "-0.01em",
                    }}
                >
                    Bekymringsmestring
                </Typography>
            </Box>

            <IconButton
                onClick={handleClose}
                aria-label="Lukk meny"
                size="small"
                sx={{
                    color: "text.secondary",
                    "&:hover": {color: "primary.main"},
                }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        </Box>
    );

    return (
        <Box
            component="nav"
            aria-label="Mobilmeny"
            sx={{
                display: {xs: "block", lg: "none"},
            }}
        >
            <SwipeableDrawer
                anchor="left"
                open={menuOpen}
                onClose={() => dispatch({type: MENU_CLOSED})}
                onOpen={() => dispatch({type: MENU_OPENED})}
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 2,
                    "& .MuiDrawer-paper": {
                        width: drawerWidth,
                        boxSizing: "border-box",
                        overflowX: "hidden",
                        overflowY: "auto",
                        bgcolor: "#FAF8F5",
                        scrollbarWidth: "none",
                        "&::-webkit-scrollbar": {
                            display: "none",
                        },
                    },
                }}
                ModalProps={{
                    keepMounted: true,
                }}
            >
                <Box sx={{width: drawerWidth, pb: 4}}>
                    {renderHeader()}
                    {userStatusCard()}
                    {menuList}
                    {adminMenu()}
                </Box>
            </SwipeableDrawer>
        </Box>
    );
};
